import { useNavigate, Link } from "react-router-dom";
import { HitLoadingBlock } from "@/components/loading";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { publicImageUrl, cardImageUrl, cardName } from "@/lib/api";
import { Trash2, ShoppingCart, ArrowRight, Truck, MapPin, HandshakeIcon, Info } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { isSealed, sealedLabel, sealedPillClass } from "@/lib/productType";

const LANG = { es: "ES", en: "EN", ja: "JP", otro: "Otro" };

export default function Cart() {
  const { cart, loading, remove } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  // Per-seller delivery choice: "shipping" | "meetup"
  const [delivery, setDelivery] = useState({});

  const goCheckout = () => {
    if (!user) { nav("/login"); return; }
    if (user.verification_status !== "aprobado") { toast.error("Debes verificar tu identidad primero"); nav("/verify"); return; }
    // Pass delivery choice via sessionStorage so Checkout can read it without a bigger refactor
    sessionStorage.setItem("hittcg_cart_delivery", JSON.stringify(delivery));
    nav("/checkout");
  };

  const setMethod = (sid, m) => setDelivery((d) => ({ ...d, [sid]: m }));

  if (loading) return <div className="max-w-4xl mx-auto px-5 py-8 min-h-[70vh]"><HitLoadingBlock section="cart" /></div>;

  if (!cart.groups.length) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center">
        <ShoppingCart size={48} className="text-neutral-700 mx-auto mb-4" />
        <h1 className="font-display text-3xl mb-2">CARRITO VACÍO</h1>
        <p className="text-neutral-500 mb-6">Aún no has agregado cartas al carrito.</p>
        <Button data-testid="cart-empty-catalog" onClick={() => nav("/catalog")} className="bg-[#EF4444] hover:bg-[#DC2626]">Explorar catálogo</Button>
      </div>
    );
  }

  // Compute displayed grand total considering meetup ($0 ship).
  // Model B: buyer pays exactly subtotal + shipping (no gross-up).
  const displayedTotal = cart.groups.reduce((sum, g) => {
    const m = delivery[g.seller.id] || (g.shipping_available === false && g.meetup_available ? "meetup" : "shipping");
    const ship = m === "meetup" ? 0 : g.ship_cost;
    return sum + (g.buyer_subtotal ?? g.subtotal) + ship;
  }, 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-6">CARRITO ({cart.count})</h1>

      {!cart.buyer_province && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md text-sm text-amber-300 flex items-start gap-2" data-testid="cart-no-province">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>Aún no has definido tu provincia. Los envíos se cotizarán como “otras provincias” por defecto. Ve a <button onClick={() => nav("/verify")} className="underline">tu perfil</button> para actualizarla.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          {cart.groups.map((g) => {
            const method = delivery[g.seller.id] || (g.shipping_available === false && g.meetup_available ? "meetup" : "shipping");
            return (
            <div key={g.seller.id} data-testid={`cart-seller-${g.seller.id}`} className="bg-[#111] border border-[#262626] rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-medium">Vendedor: <Link to={`/vendedor/${g.seller.id}`} data-testid={`cart-seller-link-${g.seller.id}`} className="text-white hover:text-[#EF4444] underline-offset-2 hover:underline">{g.seller.name}</Link></div>
                  <div className="text-xs text-neutral-500 flex items-center gap-1">
                    <MapPin size={12} />
                    {g.seller.city ? `${g.seller.city}, ${g.seller.province || ""}` : (g.seller.province || "—")}
                    {g.same_city && g.same_province && <span className="ml-2 text-emerald-400">· Misma ciudad</span>}
                    {g.same_province && !g.same_city && <span className="ml-2 text-amber-400">· Misma provincia</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-neutral-500">Envío</div>
                  <div className="font-display text-lg text-white">
                    {method === "meetup" ? (
                      <span className="text-emerald-400 text-sm inline-flex items-center gap-1"><HandshakeIcon size={14} /> ENCUENTRO</span>
                    ) : g.ship_cost === 0 ? (
                      <span className="text-emerald-400 text-sm inline-flex items-center gap-1"><Truck size={14} /> GRATIS</span>
                    ) : `$${g.ship_cost.toFixed(2)}`}
                  </div>
                </div>
              </div>

              {g.shipping_available === false && (
                <div className={`px-4 py-2.5 border-b border-[#262626] text-xs ${g.meetup_available ? "bg-amber-500/5 text-amber-200" : "bg-red-500/10 text-red-300"}`} data-testid={`cart-meetup-only-${g.seller.id}`}>
                  {g.meetup_available ? "Este vendedor solo entrega en encuentro presencial." : "Este vendedor solo entrega en encuentro presencial en su ciudad. Quita sus productos para poder pagar."}
                </div>
              )}
              {g.local_only_province && (
                <div className={`px-4 py-2.5 border-b border-[#262626] text-xs ${g.local_blocked ? "bg-red-500/10 text-red-300" : "bg-amber-500/5 text-amber-200"}`} data-testid={`cart-local-only-${g.seller.id}`}>
                  {g.local_blocked
                    ? <>Este vendedor solo envía dentro de <b>{g.local_only_province}</b>. Quita sus productos para poder pagar.</>
                    : <>Este vendedor solo envía dentro de <b>{g.local_only_province}</b>.</>}
                </div>
              )}
              {g.meetup_available && (
                <div className="px-4 py-3 bg-[#0A0A0A] border-b border-[#262626] text-sm" data-testid={`cart-meetup-${g.seller.id}`}>
                  <div className="text-emerald-400 mb-2 font-medium flex items-center gap-2"><HandshakeIcon size={14} /> Encuentro presencial disponible (misma ciudad)</div>
                  {g.meetup_notes && <div className="text-xs text-neutral-400 mb-2">{g.meetup_notes}</div>}
                  <div className="flex gap-2 flex-wrap">
                    {g.shipping_available !== false && <button
                      type="button"
                      data-testid={`cart-method-shipping-${g.seller.id}`}
                      onClick={() => setMethod(g.seller.id, "shipping")}
                      className={`px-3 py-1.5 rounded text-xs border ${method === "shipping" ? "bg-[#EF4444] border-[#EF4444] text-white" : "border-[#262626] text-neutral-400 hover:border-[#EF4444]"}`}
                    >
                      Envío por courier (${g.ship_cost.toFixed(2)})
                    </button>}
                    <button
                      type="button"
                      data-testid={`cart-method-meetup-${g.seller.id}`}
                      onClick={() => setMethod(g.seller.id, "meetup")}
                      className={`px-3 py-1.5 rounded text-xs border ${method === "meetup" ? "bg-emerald-600 border-emerald-600 text-white" : "border-[#262626] text-neutral-400 hover:border-emerald-500"}`}
                    >
                      Encuentro presencial ($0)
                    </button>
                  </div>
                  {method === "meetup" && (
                    <div className="mt-2 text-[11px] text-neutral-400">
                      El pago sigue retenido. Tras pagar podrás ver el teléfono del vendedor. El vendedor sube evidencia y tú confirmas la entrega en la web.
                    </div>
                  )}
                </div>
              )}

              <div className="divide-y divide-[#171717]">
                {g.items.map((it) => {
                  const sealed = isSealed(it);
                  return (
                  <div key={it.id} data-testid={`cart-item-${it.publication_id}`} className={`flex items-center gap-3 p-3 border-l-2 ${sealed ? "border-l-purple-500" : "border-l-transparent"}`}>
                    <div className={`w-12 h-16 bg-[#0A0A0A] rounded overflow-hidden shrink-0 border ${sealed ? "border-purple-500/60" : "border-transparent"}`}>
                      {it.card?.image_url ? <img src={cardImageUrl(it.card.image_url)} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} /> :
                       it.publication.photos?.[0] ? <img src={publicImageUrl(it.publication.photos[0])} alt="" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.display = "none"} /> :
                       <div className="w-full h-full flex items-center justify-center text-[9px] text-neutral-600">Sin foto</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="text-sm line-clamp-1 flex-1 min-w-0">{cardName(it.card, it.publication?.language)}</div>
                        {sealed && (
                          <span className={sealedPillClass} data-testid={`cart-sealed-${it.publication_id}`}>
                            📦 {sealedLabel(it)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500">{LANG[it.publication.language]} · {it.publication.condition} · Cantidad: {it.quantity}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-display text-lg ${sealed ? "text-purple-300" : "text-[#EF4444]"}`}>${Number(it.buyer_pays_line ?? (it.publication.buyer_pays ?? it.publication.price) * it.quantity).toFixed(2)}</div>
                      <div className="text-[10px] text-neutral-600">Precio final</div>
                    </div>
                    <Button data-testid={`cart-remove-${it.publication_id}`} size="sm" variant="ghost" onClick={() => remove(it.publication_id)} className="text-neutral-500 hover:text-[#EF4444]">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  );
                })}
              </div>
              <div className="px-4 py-2 bg-[#0A0A0A] flex justify-between text-sm">
                <span className="text-neutral-500">Subtotal vendedor</span>
                <span className="text-white font-medium">${((g.buyer_subtotal ?? g.subtotal) + (method === "meetup" ? 0 : g.ship_cost)).toFixed(2)}</span>
              </div>
            </div>
            );
          })}
        </div>

        <div className="bg-[#111] border border-[#262626] rounded-md p-6 h-fit md:sticky md:top-24">
          <div className="text-neutral-500 text-xs uppercase tracking-wider mb-3">Total a pagar</div>
          <div className="font-display text-4xl text-[#EF4444] mb-4" data-testid="cart-grand-total">${displayedTotal.toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mb-6">Cada vendedor genera un sub-pedido con su propio envío, timer y disputa.</div>
          {cart.groups.some((g) => (g.shipping_available === false && !g.meetup_available) || g.local_blocked) && (
            <div className="text-xs text-red-300 mb-3" data-testid="cart-blocked-note">Quita los productos marcados en rojo para poder pagar.</div>
          )}
          <Button data-testid="cart-checkout" onClick={goCheckout} disabled={cart.groups.some((g) => (g.shipping_available === false && !g.meetup_available) || g.local_blocked)} className="w-full bg-[#EF4444] hover:bg-[#DC2626] h-12">
            Continuar al checkout <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
